import type { Components, CSSObject, Theme } from '@mui/material/styles';

import { type SurfaceLook, type SurfaceTone, TINT_SCALES } from './looks';
import { ELEVATION, SHAPE, tinted, token } from './scales';

const RADII: Record<SurfaceLook['radius'], number> = {
  none: 0,
  sm: SHAPE.small,
  md: SHAPE.medium,
  lg: SHAPE.large,
  full: SHAPE.full,
};

/** The fill of each surface tone that is not a tint. */
const FILLS: Partial<Record<SurfaceTone, (theme: Theme) => CSSObject>> = {
  surface: (theme) => ({ backgroundColor: theme.vars.palette.background.paper }),
  page: (theme) => ({ backgroundColor: theme.vars.palette.background.default }),
  subtle: (theme) => ({ backgroundColor: theme.vars.palette.grey[100] }),
  filled: (theme) => ({ color: theme.vars.palette.primary.contrastText, backgroundColor: theme.vars.palette.primary.main }),
  creative: () => ({ backgroundColor: token('creative-surface') }),
  scrim: () => ({ backgroundColor: token('media-scrim') }),
  glass: () => ({ backgroundColor: token('on-filled', 0.2), backdropFilter: 'blur(8px)' }),
  workspace: () => ({
    backgroundColor: token('workspace'),
    backgroundImage: `radial-gradient(${token('neutral-900', 0.18)} 1px, transparent 1px)`,
    backgroundSize: '16px 16px',
  }),
};

/** Returns the brand fill and border of a chosen or hovered surface. */
function chosen(theme: Theme) {
  return { backgroundColor: theme.vars.palette.tint.brand.fill, borderColor: theme.vars.palette.tint.brand.border };
}

/** The kit surfaces: paper, cards, kit surfaces, the top bar and the action bar. */
export const SURFACES: Components<Theme> = {
  MuiPaper: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: { backgroundImage: 'none' },
      rounded: { borderRadius: SHAPE.medium },
    },
  },
  MuiCard: {
    defaultProps: { variant: 'outlined' },
    styleOverrides: { root: { overflow: 'visible' } },
  },
  KitCard: {
    variants: [
      {
        props: { glow: true },
        style: ({ theme }) => ({
          borderColor: token('brand-600', 0.35),
          boxShadow: `0 0 0 3px ${token('brand-600', 0.08)}, 0 4px 16px ${token('brand-600', 0.12)}`,
          ...theme.applyStyles('dark', {
            borderColor: token('brand-400', 0.45),
            boxShadow: `0 0 0 3px ${token('brand-400', 0.12)}`,
          }),
        }),
      },
      ...(['md', 'lg'] as const).flatMap((step) => [
        { props: { lift: step }, style: ({ theme }: { theme: Theme }) => ({ '&:hover': { translate: `0 ${theme.spacing(-0.25)}` } }) },
        { props: { lift: step, glow: false }, style: { '&:hover': { boxShadow: ELEVATION[step] } } },
      ]),
    ],
  },
  KitSurface: {
    styleOverrides: {
      root: ({ theme }) => ({ borderWidth: 0, borderStyle: 'solid', borderColor: theme.vars.palette.divider }),
    },
    variants: [
      ...Object.entries(FILLS).map(([tone, fill]) => ({
        props: { tone: tone as SurfaceTone },
        style: ({ theme }: { theme: Theme }) => fill(theme),
      })),
      ...TINT_SCALES.map((scale) => ({
        props: { tone: scale },
        style: ({ theme }: { theme: Theme }) => tinted(theme, scale),
      })),
      { props: { outline: 'solid' }, style: { borderWidth: 1 } },
      { props: { outline: 'dashed' }, style: { borderWidth: 1, borderStyle: 'dashed' } },
      ...Object.entries(RADII).map(([radius, px]) => ({
        props: { radius: radius as SurfaceLook['radius'] },
        style: { borderRadius: px },
      })),
      ...Object.entries(ELEVATION).map(([elevation, shadow]) => ({
        props: { elevation: elevation as SurfaceLook['elevation'] },
        style: { boxShadow: shadow },
      })),
      { props: { dimmed: 'some' }, style: { opacity: 0.6 } },
      { props: { dimmed: 'half' }, style: { opacity: 0.5 } },
      { props: { clickable: true }, style: { cursor: 'pointer', '&:disabled': { cursor: 'not-allowed', opacity: 0.6 } } },
      { props: { interactive: 'brand' }, style: ({ theme }) => ({ '&:hover': chosen(theme) }) },
      {
        props: { interactive: 'quiet', selected: false },
        style: ({ theme }) => ({ '&:hover': { backgroundColor: theme.vars.palette.action.hover } }),
      },
      { props: { selected: true }, style: ({ theme }) => chosen(theme) },
    ],
  },
  MuiAppBar: {
    defaultProps: { position: 'sticky', color: 'inherit', elevation: 0 },
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.vars.palette.text.primary,
        backgroundColor: theme.vars.palette.background.paper,
        borderBottom: `1px solid ${theme.vars.palette.divider}`,
      }),
    },
  },
  KitActionBar: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.vars.palette.background.default,
        borderTop: `1px solid ${theme.vars.palette.divider}`,
        [theme.breakpoints.up('lg')]: { backgroundColor: 'transparent', borderTop: 0 },
      }),
    },
  },
};
